import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Landmark, Copy, Check, HandHeart, Megaphone, Users, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import SectionHeader from "@/components/site/SectionHeader";
import { submitVolunteer } from "@/lib/api";

const BANK = [
  { label: "Account Name", value: "Aoturoa Academy Charitable Trust" },
  { label: "Bank", value: "ASB Bank" },
  { label: "Account Number", value: "12-3077-0431371-00" },
  { label: "Reference", value: "Donation-[YOUR NAME]" },
];

const WAYS = [
  { icon: HandHeart, title: "Volunteer your time", text: "Share your skills and knowledge directly with our youth programmes — educators, designers, facilitators and more." },
  { icon: Megaphone, title: "Amplify the kaupapa", text: "Follow, share and tag us on social media to help reach more rangatahi and whānau across Aotearoa." },
  { icon: Users, title: "Connect us to your network", text: "Introduce us to schools, kura, iwi, community groups or funders who might share our kaupapa." },
];

function CopyRow({ label, value }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy");
    }
  };
  return (
    <div className="border-b border-stone last:border-0 py-5">
      <div className="text-xs uppercase tracking-[0.18em] font-semibold text-ink/55">{label}</div>
      <div className="mt-2 flex items-center justify-between gap-4">
        <code className="font-mono text-base sm:text-lg text-ink font-medium break-all">{value}</code>
        <button
          onClick={onCopy}
          data-testid={`copy-${label.toLowerCase().replace(/\s/g, "-")}`}
          className="flex-shrink-0 w-10 h-10 rounded-full border border-stone flex items-center justify-center hover:bg-ink hover:border-ink hover:text-white transition-all"
          aria-label={`Copy ${label}`}
        >
          {copied ? <Check className="w-4 h-4 text-crimson" /> : <Copy className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

export default function Donate() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", location: "", interests: "", availability: "", message: "" });
  const [loading, setLoading] = useState(false);
  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitVolunteer(form);
      toast.success("Ngā mihi! We'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", location: "", interests: "", availability: "", message: "" });
    } catch {
      toast.error("Couldn't send. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <>
      <section className="container-x pt-20 pb-12 sm:pt-28">
        <div className="max-w-4xl">
          <span className="label-eyebrow">— Support our mahi</span>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-7xl font-heading font-semibold tracking-tighter leading-[1.02] text-balance">
            Your contribution helps us reach more rangatahi.
          </h1>
          <p className="mt-8 text-lg text-ink/65 max-w-2xl leading-relaxed">
            We are a registered charitable trust (CC58012) supporting youth wellbeing and sexual health education
            across Aotearoa. Every contribution — large or small — helps us continue this kaupapa.
          </p>
        </div>
      </section>

      {/* BANK DETAILS */}
      <section className="container-x pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          <div className="lg:col-span-5">
            <div className="bg-ink text-bone rounded-3xl p-8 sm:p-10 sticky top-28">
              <Landmark className="w-8 h-8 text-crimson mb-6" />
              <span className="label-eyebrow">— Bank transfer</span>
              <h2 className="mt-3 text-3xl font-heading font-semibold leading-tight">Direct giving via NZ bank transfer.</h2>
              <p className="mt-5 text-bone/70 leading-relaxed">
                The simplest way to support our mahi today is a one-off or recurring transfer to our charitable trust
                account. Please use the reference below so we can thank you personally.
              </p>
              <p className="mt-7 text-xs text-bone/45 italic leading-relaxed">
                Aoturoa Academy is a registered charitable trust (CC58012). All donations over $5 are eligible for tax
                deductions in Aotearoa New Zealand.
              </p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-white border border-stone rounded-3xl p-8 sm:p-10" data-testid="bank-details-card">
              <h3 className="text-2xl font-heading font-semibold text-ink">Donation Details</h3>
              <p className="text-sm text-ink/55 mt-1">Bank transfer information</p>
              <div className="mt-7">
                {BANK.map((b) => (
                  <CopyRow key={b.label} {...b} />
                ))}
              </div>
              <Link to="/contact" data-testid="donate-confirm-btn" className="mt-8 btn-primary inline-flex">
                Let us know you've donated <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* OTHER WAYS TO HELP */}
      <section className="bg-crimson text-white">
        <div className="container-x py-20 lg:py-24">
          <div className="max-w-3xl">
            <span className="label-eyebrow !text-white/70">— Other ways to help</span>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-heading font-semibold tracking-tight leading-[1.05] text-balance">
              Beyond financial contributions.
            </h2>
            <p className="mt-5 text-white/80 max-w-xl">Time, voice, and connection are just as valuable as koha. Here's how else you can stand with us.</p>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
            {WAYS.map(({ icon: Icon, title, text }) => (
              <div key={title} className="bg-white/10 border border-white/15 rounded-2xl p-7 hover:bg-white/15 transition-all">
                <Icon className="w-6 h-6 text-white mb-5" />
                <h3 className="font-heading font-semibold text-lg leading-snug">{title}</h3>
                <p className="mt-2 text-white/80 text-sm leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VOLUNTEER EOI */}
      <section className="section-y">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeader eyebrow="Volunteer with us" title="Express your interest." description="Whether you're an educator, health professional, designer, or simply someone who cares — we'd love to hear how you'd like to be involved." />
            <div className="mt-8 space-y-3 text-ink/75">
              {["Workshop facilitation","Resource creation & design","Community engagement","Peer leadership mentoring","Translation & bilingual support"].map((r) => (
                <div key={r} className="flex items-center gap-3"><span className="w-1.5 h-1.5 rounded-full bg-crimson" /><span>{r}</span></div>
              ))}
            </div>
          </div>
          <form onSubmit={onSubmit} className="lg:col-span-7 bg-white rounded-3xl p-8 sm:p-10 border border-stone" data-testid="volunteer-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="text-sm font-medium text-ink/80 mb-2 block">Name</label>
                <input required className="input-field" value={form.name} onChange={onChange("name")} data-testid="volunteer-name" />
              </div>
              <div>
                <label className="text-sm font-medium text-ink/80 mb-2 block">Email</label>
                <input required type="email" className="input-field" value={form.email} onChange={onChange("email")} data-testid="volunteer-email" />
              </div>
              <div>
                <label className="text-sm font-medium text-ink/80 mb-2 block">Phone (optional)</label>
                <input className="input-field" value={form.phone} onChange={onChange("phone")} data-testid="volunteer-phone" />
              </div>
              <div>
                <label className="text-sm font-medium text-ink/80 mb-2 block">Location</label>
                <input className="input-field" value={form.location} onChange={onChange("location")} data-testid="volunteer-location" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-sm font-medium text-ink/80 mb-2 block">Areas of interest</label>
                <input className="input-field" placeholder="e.g. facilitation, design, peer mentoring" value={form.interests} onChange={onChange("interests")} data-testid="volunteer-interests" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-sm font-medium text-ink/80 mb-2 block">Availability</label>
                <input className="input-field" placeholder="e.g. weekends, 2 hrs/week" value={form.availability} onChange={onChange("availability")} data-testid="volunteer-availability" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-sm font-medium text-ink/80 mb-2 block">Anything else?</label>
                <textarea rows={4} className="input-field resize-none" value={form.message} onChange={onChange("message")} data-testid="volunteer-message" />
              </div>
            </div>
            <button type="submit" disabled={loading} className="mt-7 btn-crimson" data-testid="volunteer-submit">
              {loading ? "Sending..." : <>Express interest <ArrowUpRight className="w-4 h-4" /></>}
            </button>
          </form>
        </div>
      </section>
    </>
  );
}
